import type { CityConfig } from "@/pages/city-template/types";

export const middleburyConfig: CityConfig = {
  city: "Middlebury",
  state: "Vermont",
  stateAbbr: "VT",
  slug: "middlebury",
  route: "/cities/middlebury",
  title: "Expedited Passport Services in Middlebury, VT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Middlebury, VT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered courier serving Middlebury, Bristol, New Haven, Weybridge, and all of Addison County.",
  h1: "Expedited Passport Services in Middlebury, VT",
  heroSubtitle:
    "Middlebury travelers trust Fast Passport Center for emergency and expedited passports — from 24-hour emergency turnaround to 10-day Smart Saver. No trip to the New York Passport Agency required.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Charming%20view%20of%20Middlebury%20Vermont%20downtown%20featuring%20historic%20Middlebury%20College%20campus%20with%20classic%20stone%20buildings%20and%20iconic%20chapel%2C%20Otter%20Creek%20flowing%20through%20the%20village%2C%20tree-lined%20streets%20with%20classic%20New%20England%20architecture%2C%20green%20rolling%20hills%20of%20Addison%20County%20in%20the%20background%2C%20warm%20golden%20hour%20light%2C%20picturesque%20Vermont%20college%20town%2C%20professional%20travel%20photography%2C%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=vt-mid-hero-001&orientation=landscape",
  heroImageAlt: "Middlebury Vermont downtown with historic college campus and Otter Creek",
  geoPosition: "44.0153; -73.1673",
  geoPlacename: "Middlebury, Vermont, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "New York Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "~220 miles from Middlebury via I-87",
  },
  nearbyCitiesZips: [
    { name: "Middlebury", zips: "05753" },
    { name: "Middlebury College", zips: "05753" },
    { name: "Bristol", zips: "05443" },
    { name: "New Haven", zips: "05472" },
    { name: "Weybridge", zips: "05753" },
    { name: "Shoreham", zips: "05770" },
    { name: "Vergennes", zips: "05491" },
    { name: "Ferrisburgh", zips: "05456" },
    { name: "Cornwall", zips: "05753" },
    { name: "Ripton", zips: "05766" },
    { name: "Salisbury", zips: "05769" },
    { name: "Goshen", zips: "05733" },
    { name: "Brandon", zips: "05733" },
    { name: "Leicester", zips: "05733" },
    { name: "Whiting", zips: "05778" },
    { name: "Rutland", zips: "05701, 05702" },
    { name: "Burlington", zips: "05401, 05402, 05405, 05406" },
    { name: "South Burlington", zips: "05403, 05407" },
    { name: "Montpelier", zips: "05601, 05602, 05603, 05604, 05609, 05620, 05633" },
    { name: "Stowe", zips: "05672" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Middlebury travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Addison County",
      desc: "Middlebury, Bristol, New Haven, Weybridge, and all surrounding Addison County communities.",
    },
  ],
  testimonials: [
    {
      name: "Emma D.",
      location: "Middlebury, VT",
      rating: 5,
      text: "I needed my passport renewed for a study abroad program in 5 days. Fast Passport Center had it back to me in Middlebury in 3 days. Their Addison County service is outstanding.",
      service: "Passport Renewal",
    },
    {
      name: "James R.",
      location: "Bristol, VT",
      rating: 5,
      text: "Applied for my first passport through Fast Passport Center. They helped me navigate the process from Bristol and I had my passport in 4 days. Great experience.",
      service: "New Passport",
    },
    {
      name: "Sarah K.",
      location: "New Haven, VT",
      rating: 5,
      text: "My son needed a passport for a school trip to Quebec. Fast Passport Center handled the child passport process and had it ready in 4 days. New Haven families can trust them.",
      service: "Child Passport",
    },
    {
      name: "Robert L.",
      location: "Weybridge, VT",
      rating: 5,
      text: "Lost my passport before a business trip to Europe. Fast Passport Center replaced it in 3 days and saved my trip. Weybridge residents should use them for emergencies.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "mid-1",
      name: "Middlebury Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "10 Main Street",
      city: "Middlebury",
      state: "VT",
      zip: "05753",
      phone: "802-388-6461",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Middlebury",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "mid-2",
      name: "Bristol Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "50 West Street",
      city: "Bristol",
      state: "VT",
      zip: "05443",
      phone: "802-453-3844",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Bristol",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "mid-3",
      name: "Vergennes Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "21 Green Street",
      city: "Vergennes",
      state: "VT",
      zip: "05491",
      phone: "802-877-2172",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Vergennes",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
    {
      id: "mid-4",
      name: "New Haven Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "1175 New Haven Road",
      city: "New Haven",
      state: "VT",
      zip: "05472",
      phone: "802-877-2131",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "New Haven",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-building-2-line",
    },
    {
      id: "mid-5",
      name: "Shoreham Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "27 Main Street",
      city: "Shoreham",
      state: "VT",
      zip: "05770",
      phone: "802-897-5513",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Shoreham",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-file-shield-line",
    },
    {
      id: "mid-6",
      name: "Middlebury Town Clerk",
      type: "Town Clerk / Passport Services",
      address: "77 Main Street",
      city: "Middlebury",
      state: "VT",
      zip: "05753",
      phone: "802-458-2741",
      hours: "Mon-Fri 8:00 AM - 4:30 PM",
      distance: "Middlebury Town Offices",
      website: "https://www.middleburyvt.org/",
      websiteLabel: "Town of Middlebury",
      notes: "Provides passport application acceptance services. Appointment recommended.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Middlebury, VT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the New York Passport Agency if I live in Middlebury?",
      a: "No — if you use Fast Passport Center, you don't need to visit the New York Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in Middlebury?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Middlebury, Bristol, and Vergennes. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Middlebury?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Middlebury?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Middlebury?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Middlebury. We serve Middlebury and all of Addison County through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
    {
      q: "What is the nearest airport to Middlebury for international travel?",
      a: "Burlington International (BTV) is the closest at ~35 miles. Albany International (ALB) is ~100 miles, and Boston Logan (BOS) is ~200 miles. Most Middlebury residents use Burlington or Albany for international travel.",
    },
    {
      q: "How much does expedited passport service cost in Middlebury?",
      a: "Government fees are $130-$165 for adult passports, plus a $60 expedited fee. Fast Passport Center's service fees vary by processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do both parents need to be present for a child passport in Middlebury?",
      a: "Yes, both parents or legal guardians must appear with the minor. If one parent cannot attend, a notarized DS-3053 Statement of Consent is required. Fast Passport Center provides guidance on all parental consent documentation.",
    },
    {
      q: "Can I get a passport the same day in Middlebury?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel. Fast Passport Center's 24-hour emergency service is the fastest option available through a private courier.",
    },
    {
      q: "What should I do if my passport is lost or stolen in Middlebury?",
      a: "Report it immediately using Form DS-64 and apply for a replacement using Form DS-11. Fast Passport Center can help you file the report and process your replacement passport in 3-5 days.",
    },
    {
      q: "Does Middlebury have a local passport agency?",
      a: "No, Middlebury does not have a regional passport agency. The nearest regional agency is the New York Passport Agency in Manhattan, approximately 220 miles away. Fast Passport Center serves as your courier to this agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Middlebury", address: "various CVS locations in Middlebury", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Middlebury", address: "various Walgreens locations in Middlebury", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Middlebury", address: "various UPS Store locations in Middlebury", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Middlebury", address: "various FedEx Office locations in Middlebury", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Vergennes", address: "various Walmart locations in Vergennes", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "VT DMV — Middlebury",
      address: "1560 Route 7 South, Middlebury, VT 05753",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications. Closest DMV to Middlebury.",
      featured: true,
    },
    {
      name: "VT DMV — Rutland",
      address: "92 State Street, Rutland, VT 05701",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Alternative DMV location for Addison County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BTV",
      name: "Burlington International Airport",
      distance: "~35 miles from Middlebury",
      address: "1200 Airport Dr, South Burlington, VT 05403",
      note: "Vermont's primary airport. Offers domestic flights and seasonal international connections. Most Middlebury residents use BTV.",
      flightUrl: "https://www.btv.aero/",
      flightLabel: "Burlington International",
      featured: true,
    },
    {
      code: "ALB",
      name: "Albany International Airport",
      distance: "~100 miles from Middlebury",
      address: "737 Albany Shaker Rd, Albany, NY 12211",
      note: "Alternative for Middlebury travelers with Southwest and other domestic carriers.",
      flightUrl: "https://www.albanyairport.com/",
      flightLabel: "Albany International",
      featured: false,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "~200 miles from Middlebury",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with flights to virtually every global destination. Common for Middlebury residents on international trips.",
      flightUrl: "https://www.massport.com/logan-airport/",
      flightLabel: "Logan International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Middlebury travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Middlebury residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Middlebury residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Middlebury residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Middlebury, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Middlebury residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Middlebury travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Middlebury residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Middlebury residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Middlebury residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Middlebury." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Middlebury." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Middlebury travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Middlebury travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Middlebury team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Middlebury address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: ["Mail-in application", "No expedited handling", "No dedicated agent", "No phone or email contact for your case", "Updates only by physical mail letter"],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: ["For documented life-or-death emergencies only", "Requires proof of emergency travel", "Strictly for documented life-or-death emergencies"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Burlington", route: "/cities/burlington-vt" },
    { name: "Montpelier", route: "/cities/montpelier" },
    { name: "Rutland", route: "/cities/rutland" },
    { name: "Stowe", route: "/cities/stowe" },
    { name: "Brattleboro", route: "/cities/brattleboro" },
    { name: "Bennington", route: "/cities/bennington" },
    { name: "Middlebury", route: "/cities/middlebury" },
    { name: "Woodstock", route: "/cities/woodstock-vt" },
    { name: "Springfield", route: "/cities/springfield-vt" },
    { name: "White River Junction", route: "/cities/white-river-junction" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Middlebury and all of Addison County as a remote courier service. We do not have a physical office in Middlebury, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeDistance: "Remote courier service — Middlebury, VT area",
  serviceAreaIntro: "Fast Passport Center provides expedited passport services to all Middlebury neighborhoods and surrounding Addison County communities. From the college campus to the village center, from the Otter Creek valley to the rolling farmland — we serve every Middlebury community with the same fast, reliable processing.",
  nearbyCitiesIntro: "We proudly serve Middlebury and all surrounding Addison County communities. Whether you're in downtown Middlebury or a nearby town, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro: "Our Middlebury customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro: "These are the most common mistakes Middlebury travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro: "Middlebury and Addison County travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro: "Common questions about expedited passport services in Middlebury, VT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/vermont",
  statePageName: "Vermont Passport Services",
  dmvWebsite: "https://dmv.vermont.gov",
  dmvLabel: "Vermont DMV — Official Website",
};

export default middleburyConfig;